import { Divider, Grid, HStack, useBoolean, VStack } from '@chakra-ui/react';
import { Decimal } from 'decimal.js';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';

import { ListUtils } from '../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import StyledButton from '../../../components/StyledButton';
import { useFormItems } from '../../../hooks/formItems';
import { AssignWorkInfoDaySearchResult } from '../../../models/gen/assignWorkInfoDaySearchResult';
import { Formatter } from '../../../utils/FormatUtils';
import { OutsourceMemberReportOutletContext } from '../Root';
import { GridItem } from './GridItem';
import { AssignWorkInfoProps } from './WorkReport';
/**
 *
 */
type MattersProps = AssignWorkInfoProps & {
  /**
   * 欠勤フラグ
   */
  isAbsence?: boolean;
  matterIndex?: number;
  /**
   * 現場数
   */
  matterSize: number;
  toggle: Runnable;
  onChangedRelatedToSavable: () => void;
  onChangedRelatedToDailyWork: () => void;
};

/**
 *
 * @returns
 */
const Matters: React.FunctionComponent<MattersProps> = (props) => {
  const { attendanceTypes, facilitySettings, tasks } =
    useOutletContext<OutsourceMemberReportOutletContext>();
  const ATTENDANCE_TYPE_PRESENT = React.useMemo(
    () =>
      attendanceTypes?.find((attendanceType) => attendanceType.name === '出勤')
        ?.id,
    [attendanceTypes]
  );
  // const [matterNo, setMatterNo] = React.useState(props.matterIndex || 0);
  const form = useFormContext<AssignWorkInfoDaySearchResult>();
  const {
    FormCheckbox,
    FormDatePicker,
    FormInput,
    FormInputNumber,
    FormMultiSelect,
    FormSelect,
    FormText,
    FormTextarea,
  } = useFormItems(form);
  const [recalculateMatterTrigger, { toggle: recalculateMatter }] =
    useBoolean(false);

  // React.useState(() => recalculateMatter());

  const workTimeFrom = React.useMemo(
    () => form.getValues(`results.${0}.assignedWorkerWorkInfo.workTimeFrom`),
    [form, recalculateMatterTrigger]
  );
  const workTimeTo = React.useMemo(
    () => form.getValues(`results.${0}.assignedWorkerWorkInfo.workTimeTo`),
    [form, recalculateMatterTrigger]
  );
  const workTime = React.useMemo(() => {
    // 作業時間を再計算して setValue する
    const from =
      form.getValues(`results.${0}.assignedWorkerWorkInfo.workTimeFrom`) || '';
    const to =
      form.getValues(`results.${0}.assignedWorkerWorkInfo.workTimeTo`) || '';
    const rest =
      form.getValues(`results.${0}.assignedWorkerWorkInfo.restTime`) || 0;
    const wt = (Date.parse(to) - Date.parse(from)) / (1000 * 60) - rest;
    form.setValue(`results.${0}.assignedWorkerWorkInfo.workTime`, wt);
    // ここで DailyWork 側変更を通知
    props.onChangedRelatedToDailyWork();
    return form.getValues(`results.${0}.assignedWorkerWorkInfo.workTime`) || 0;
  }, [workTimeFrom, workTimeTo, recalculateMatterTrigger]);
  const attendanceTypeId = React.useMemo(
    () =>
      form.getValues(`results.${0}.assignedWorkerWorkInfo.attendanceTypeId`),
    [form, recalculateMatterTrigger]
  );
  // 現場毎に isOvertime を考慮する
  // 残業有無(総作業時間が8時間を越える)
  const isOvertime = React.useMemo(
    () =>
      workTime > 480 &&
      !TypeUtils.isNilString(workTimeFrom) &&
      !TypeUtils.isNilString(workTimeTo),
    [workTime]
  );

  return (
    <VStack width="full">
      {/* <HStack
        height={{ base: '20px', md: 'unset' }}
        justifyContent="space-between"
      >
        <StyledButton
          fontWeight="bold"
          height="full"
          isDisabled={0 === 0}
          onClick={() => setMatterNo(Math.max(0 - 1, 0))}
          textDecoration="unset"
          variant="link"
        >
          &lt;&lt;
        </StyledButton>
        <Text fontSize={{ base: 'md', md: '2xl' }}>{0 + 1} 現場目</Text>
        <StyledButton
          fontWeight="bold"
          height="full"
          isDisabled={0 === props.matterSize - 1}
          onClick={() => setMatterNo(Math.min(0 + 1, props.matterSize - 1))}
          textDecoration="unset"
          variant="link"
        >
          &gt;&gt;
        </StyledButton>
      </HStack> */}
      <Grid
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        key={0}
        padding={2}
        templateColumns={{ base: 'unset', md: '1fr 1fr' }}
        width="full"
      >
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            name={`results.${0}.matterSummary.matterName`}
            value={(field) => (
              <StyledButton
                colorScheme="black"
                isDisabled
                textAlign="left"
                variant="link"
                whiteSpace="normal"
              >
                {field.value}
              </StyledButton>
            )}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="依頼元"
            name={`results.${0}.matterSummary.customerName`}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="最寄駅"
            name={`results.${0}.matterSummary.station`}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="営業担当"
            name={`results.${0}.matterSummary.nameSales`}
          />
        </GridItem>
        <GridItem>
          <FormText
            label="経理担当"
            name={`results.${0}.matterSummary.nameAccountant`}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormCheckbox
            checkboxProps={{
              defaultChecked: form.getValues(`results.${0}.isLeader`),
            }}
            mode="view"
            name={`results.${0}.isLeader`}
          >
            職長
          </FormCheckbox>
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormMultiSelect
            label="作業"
            mode="view"
            name={`results.${0}.assignedWorkerSummary.taskIds`}
            options={(tasks || [])
              .sort(ListUtils.sort((m) => m.displayOrder))
              .map((v) => ({
                value: v.id,
                valueToDisplay: v.name,
              }))}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            label="依頼時刻"
            name={`results.${0}.assignedWorkerSummary.requestTime`}
            value={(field) => Formatter.time(field.value, '―')}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormText
            label="依頼時メモ"
            name={`results.${0}.assignedWorkerSummary.requestMemo`}
          />
        </GridItem>

        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <Divider marginY={2} />
        </GridItem>

        <GridItem as={HStack}>
          <GridItem w="56%">
            <FormSelect<number>
              formControlProps={{ isRequired: true }}
              label="作業状況"
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${0}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${0}.assignedWorkerWorkInfo.attendanceTypeId`}
              options={(attendanceTypes || [])
                .sort(ListUtils.sort((m) => m.displayOrder))
                .filter((v) =>
                  (
                    (facilitySettings?.WORKING_REPORT_ATTENDANCE_TYPE_IDS ||
                      []) as Array<number>
                  ).includes(v.id)
                )
                .map((v) => ({
                  value: v.id,
                  valueToDisplay: v.name,
                }))}
              valueConverter={parseInt}
            />
          </GridItem>
          <GridItem
            as={HStack}
            justifyContent="start"
            hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}
          >
            <FormCheckbox
              checkboxProps={{
                defaultChecked: form.getValues(
                  `results.${0}.assignedWorkerWorkInfo.beLate`
                ),
              }}
              formControlProps={{
                width: 'unset',
              }}
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${0}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${0}.assignedWorkerWorkInfo.beLate`}
            >
              遅刻
            </FormCheckbox>
            <FormCheckbox
              checkboxProps={{
                defaultChecked: form.getValues(
                  `results.${0}.assignedWorkerWorkInfo.leaveWork`
                ),
              }}
              formControlProps={{ width: 'unset' }}
              mode={
                !TypeUtils.isNilString(
                  form.watch(
                    `results.${0}.assignedWorkerSummary.reportApprovedTime`
                  )
                )
                  ? 'view'
                  : props.mode
              }
              name={`results.${0}.assignedWorkerWorkInfo.leaveWork`}
            >
              早退
            </FormCheckbox>
          </GridItem>
        </GridItem>

        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                recalculateMatter();
                props.onChangedRelatedToSavable();
              },
            }}
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${0}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
            }}
            label="開始時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.workTimeFrom`}
            onClickClear={(name) => {
              form.setValue(name, '');
              recalculateMatter();
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                recalculateMatter();
                props.onChangedRelatedToDailyWork();
              },
            }}
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${0}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
            }}
            label="終了時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.workTimeTo`}
            onClickClear={(name) => {
              form.setValue(name, '');
              recalculateMatter();
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            formControlProps={{
              isRequired:
                form.watch(
                  `results.${0}.assignedWorkerWorkInfo.attendanceTypeId`
                ) === ATTENDANCE_TYPE_PRESENT,
              onBlur: () => {
                recalculateMatter();
                props.onChangedRelatedToSavable();
              },
            }}
            label="休憩時間（分）"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.restTime`}
            numberInputProps={{ min: 0 }}
            numberInputStepperProps={{ hidden: true }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            label="追加作業時間（分）"
            name={`results.${0}.assignedWorkerWorkInfo.restTime`}
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            readonly
            value={() => Math.max(workTime - 480, 0)}
          />
        </GridItem>
        {/* 外注は残業時間を現場ごとに計算 */}
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            label="残業時間（分）"
            name={`results.${0}.assignedWorkerWorkInfo.restTime`}
            readonly
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            value={(field) => {
              if (isOvertime && TypeUtils.isNonNilNumber(workTime)) {
                const from = Formatter.createDate(workTimeFrom!);
                const to = Formatter.createDate(workTimeTo!);
                const h22 = new Date(from);
                h22.setHours(22);
                h22.setMinutes(0);
                h22.setSeconds(0);
                h22.setMilliseconds(0);
                // 22時以前の労働時間のうち8時間を超えない部分だけ
                return Math.max(
                  new Decimal(Math.min(h22.valueOf(), to.valueOf()))
                    .minus(from.valueOf())
                    .minus(28800000)
                    .dividedToIntegerBy(60000)
                    .minus(field.value || 0)
                    .toNumber(),
                  0
                );
              }
              return 0;
            }}
          />
        </GridItem>
        <GridItem hidden={attendanceTypeId !== ATTENDANCE_TYPE_PRESENT}>
          <FormInputNumber
            label="深夜残業時間（分）"
            name="reference.dailyWork"
            readonly
            numberInputProps={{ style: { pointerEvents: 'none' } }}
            numberInputFieldProps={{ tabIndex: -1 }}
            value={() => {
              if (isOvertime) {
                const from = Formatter.createDate(workTimeFrom!);
                const to = Formatter.createDate(workTimeTo!);
                const h22 = new Date(from);
                h22.setHours(22);
                h22.setMinutes(0);
                h22.setSeconds(0);
                h22.setMilliseconds(0);
                // 労働時間のうち8時間を超えた分のうち22時以降
                return Math.max(
                  Math.min(
                    workTime - 480,
                    new Decimal(to.valueOf())
                      .minus(h22.valueOf())
                      .dividedToIntegerBy(60000)
                      .toNumber()
                  ),
                  0
                );
              }
              return 0;
            }}
          />
        </GridItem>
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            (attendanceTypeId === ATTENDANCE_TYPE_PRESENT && !isOvertime)
          }
        >
          <FormDatePicker
            datePickerProps={{
              showTimeSelect: true,
              onCalendarClose: () => {
                props.onChangedRelatedToSavable();
              },
            }}
            formControlProps={{ isRequired: isOvertime }}
            label="残業承認時刻"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.overtimeApprovedTime`}
            onClickClear={(name) => {
              form.setValue(name, '');
              props.onChangedRelatedToSavable();
            }}
          />
        </GridItem>
        <GridItem
          hidden={
            attendanceTypeId !== ATTENDANCE_TYPE_PRESENT ||
            (attendanceTypeId === ATTENDANCE_TYPE_PRESENT && !isOvertime)
          }
        >
          <FormInput
            formControlProps={{
              isRequired: isOvertime,
              onBlur: () => props.onChangedRelatedToSavable(),
            }}
            label="残業承認者"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.overtimeApprovedPerson`}
          />
        </GridItem>
        <GridItem colSpan={{ base: undefined, md: 2 }}>
          <FormTextarea
            label="備考"
            mode={
              !TypeUtils.isNilString(
                form.watch(
                  `results.${0}.assignedWorkerSummary.reportApprovedTime`
                )
              )
                ? 'view'
                : props.mode
            }
            name={`results.${0}.assignedWorkerWorkInfo.reportMemo`}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
};

export default Matters;
